"use client";

import Link from "next/link";
import { Accessibility, ArrowUpRight, BookOpen, Github, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import GradientButton from "@/components/ui/gradient-button";
import Reveal from "@/components/scroll/reveal";

// `index` is the section number, which shifts when optional sections (like
// certifications) are hidden.
export default function Closing({ index = "03" }: { index?: string }) {
    return (
        <section id="closing" data-anchor className="px-4 py-24 sm:px-16 sm:py-32">
            <div className="mx-auto w-full max-w-4xl">
                <Reveal>
                    <p className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-primary">
                        {index} — What&apos;s next
                    </p>
                </Reveal>
                <Reveal delay={0.05}>
                    <h2 className="font-heading text-5xl leading-[1.02] tracking-tight sm:text-7xl">
                        Let&apos;s build something.
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 max-w-xl text-lg text-muted-foreground">
                        Got a project, a problem, or an idea worth shipping? I&apos;m always up for the next
                        interesting build.
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="mt-9 flex flex-wrap gap-4">
                        <GradientButton href="/contact">
                            <Mail className="h-4 w-4" />
                            Get in touch
                        </GradientButton>
                        <Button size="lg" variant="outline" asChild>
                            <Link href="/blog">
                                <BookOpen className="mr-1 h-4 w-4" />
                                Read the blog
                            </Link>
                        </Button>
                        <Button size="lg" variant="outline" asChild>
                            <Link href="https://github.com/Kalaghni" target="_blank" rel="noreferrer">
                                <Github className="mr-1 h-4 w-4" />
                                GitHub
                                <ArrowUpRight className="ml-1 h-4 w-4" />
                            </Link>
                        </Button>
                    </div>
                </Reveal>
                <Reveal delay={0.2}>
                    <div className="mt-20 flex flex-col gap-3 border-t border-border/60 pt-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                        <p className="max-w-md">
                            Hit a barrier using this site? Accessibility problems are bugs, and I want to hear
                            about them.
                        </p>
                        <Link
                            href="/contact?topic=accessibility"
                            className="inline-flex w-fit items-center gap-1.5 rounded-sm font-medium text-foreground underline-offset-4 outline-none transition-[color,box-shadow] hover:text-primary hover:underline focus-visible:text-primary focus-visible:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
                        >
                            <Accessibility className="h-4 w-4" aria-hidden />
                            Report an accessibility issue
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
